import React, { MouseEventHandler, ReactNode, useCallback, useMemo, useState } from 'react'
import { preventAll } from '../../../../_utils/utils'
import { CopyTypes, JourneyAction } from '../../types/journey-context'
import { Button } from 'antd'
import { CheckCircleOutlined, CloseOutlined, EditOutlined, EyeOutlined, LoadingOutlined } from '@ant-design/icons'
import { useJourneyContext } from '../../context'
import { JourneyNode } from '../../types/journey-nodes'
import type { IActionStep, IDelayStep } from '@pushly/models/lib/structs/journeys'
import { JourneyBuilderMode } from '../../enums'
import { isSystemStep, isMeaningfulStep } from '@pushly/models/lib/structs/journeys'

export const JourneyNodeInteractionActions = ({
    nodeId,
    enriching,
    extraActions,
}: {
    nodeId: string
    enriching?: boolean
    /** NS-2599: extra hover-action buttons appended to the row (e.g. the split
     *  node's "view results" insights icon), so they live in the same action
     *  row as edit/delete and reveal on hover like the others. */
    extraActions?: ReactNode
}) => {
    const [state, dispatch] = useJourneyContext()
    const nodes = Array.isArray(state.nodes) ? state.nodes : []

    const node = nodes.find((n) => n.id === nodeId)
    if (!node) {
        return null
    }

    const [isCopied, setIsCopied] = useState(false)

    const handleRemoveNode: MouseEventHandler<HTMLElement> = useCallback(
        (ev) => {
            preventAll(ev)

            dispatch({ action: JourneyAction.CLOSE_ALL_POPOVERS })
            dispatch({ action: JourneyAction.ON_NODE_REMOVE, data: node })
        },
        [node, dispatch],
    )

    const handleCopyNode = useCallback(
        (ev) => {
            preventAll(ev)

            if (isMeaningfulStep(node.data.step.type)) {
                dispatch({
                    action: JourneyAction.SET_COPY_STATE,
                    data: { type: CopyTypes.COPY, node: node as JourneyNode<IActionStep | IDelayStep> },
                })

                setIsCopied(true)
                setTimeout(() => setIsCopied(false), 2000)
            }
        },
        [nodeId, node],
    )

    const handleCutNode = useCallback(
        (ev) => {
            preventAll(ev)
            dispatch({
                action: JourneyAction.SET_COPY_STATE,
                data: { type: CopyTypes.CUT, node: node as JourneyNode<IActionStep | IDelayStep> },
            })
            handleRemoveNode(ev)
        },
        [nodeId, node],
    )

    const handleNodeClick: MouseEventHandler<HTMLElement> = useCallback(
        (ev) => {
            if (!isSystemStep(node.data.step.type)) {
                dispatch({ action: JourneyAction.CLOSE_ALL_POPOVERS })
                dispatch({ action: JourneyAction.SET_ACTIVE_NODE, data: node })
            }
        },
        [state.mode, node],
    )

    const nodeInteractionButtons = useMemo(() => {
        if (isSystemStep(node.data.step.type)) {
            return []
        }

        const shownButtons: ReactNode[] = []

        if (state.mode === JourneyBuilderMode.READONLY) {
            shownButtons.push(
                <Button
                    key={`jnv-btn-${nodeId}`}
                    className="journey-node-view-button"
                    onClick={handleNodeClick}
                    icon={<EyeOutlined />}
                    aria-label="View Node"
                />,
            )

            return shownButtons
        }

        if (node.data.uiMeta.invalid?.options?.includes('edit') || node.data.uiMeta.invalid === undefined) {
            shownButtons.push(
                <Button
                    key={`jne-btn-${nodeId}`}
                    className="journey-node-edit-button"
                    disabled={enriching}
                    onClick={handleNodeClick}
                    aria-label="Edit node"
                    icon={enriching ? <LoadingOutlined /> : <EditOutlined />}
                />,
            )
        }

        // @todo - come back to this and figure proper handling
        // of copied and updating notifications
        // if (!node.data.uiMeta.invalid) {
        //     shownButtons.push(
        //         <Button
        //             key={`jnc-btn-${nodeId}`}
        //             className="journey-node-copy-button"
        //             icon={<CopyOutlined onClick={handleCopyNode} />}
        //         />,
        //         <Button
        //             key={`jnct-btn-${nodeId}`}
        //             className="journey-node-cut-button"
        //             icon={<ScissorOutlined onClick={handleCutNode} />}
        //         />,
        //     )
        // }

        shownButtons.push(
            <Button
                key={`jnr-btn-${nodeId}`}
                className="journey-node-remove-button"
                onClick={handleRemoveNode}
                icon={<CloseOutlined />}
                aria-label="Remove node"
            />,
        )
        return shownButtons
    }, [nodeId, node, node.data.uiMeta.invalid, enriching])

    if (nodeInteractionButtons.length === 0 && !extraActions) {
        return null
    }

    return (
        <div className={`custom-node-interaction-actions fade-in-out ${isCopied ? 'transition-copied' : ''}`}>
            {isCopied && (
                <span className="transition-copied">
                    <CheckCircleOutlined /> <span>Copied!</span>
                </span>
            )}
            {nodeInteractionButtons}
            {extraActions}
        </div>
    )
}
